let floatingTooltip = null;

function showFloatingTooltip(el) {
  if (el.scrollWidth <= el.clientWidth) return;
  const text = el.textContent.trim();
  if (!text) return;

  if (!floatingTooltip || !floatingTooltip.isConnected) {
    floatingTooltip = document.createElement('div');
    floatingTooltip.className = 'loomly-ai-floating-tooltip';
    document.body.appendChild(floatingTooltip);
  }
  floatingTooltip.textContent = text;
  floatingTooltip.style.display = 'block';

  const rect = el.getBoundingClientRect();
  floatingTooltip.style.top = (rect.top + rect.height / 2 - floatingTooltip.offsetHeight / 2) + 'px';
  floatingTooltip.style.left = (rect.right + 8) + 'px';
}

function hideFloatingTooltip() {
  if (floatingTooltip) floatingTooltip.style.display = 'none';
}

let tooltipDismissalBound = false;

// The tooltip lives on body, outside the frame Turbo swaps.
function bindTooltipDismissal() {
  if (tooltipDismissalBound) return;
  tooltipDismissalBound = true;

  document.addEventListener('turbo:before-cache', hideFloatingTooltip);
  document.addEventListener('turbo:visit', hideFloatingTooltip);
}

// --- Search ---
//
// Client-side filter over the rendered rows, collapsed groups included.

const CALENDAR_SEARCH_DIACRITICS = /[\u0300-\u036f]/g;

function normalizeCalendarSearch(value) {
  return (value || '')
    .toString()
    .normalize('NFD')
    .replace(CALENDAR_SEARCH_DIACRITICS, '')
    .toLowerCase()
    .trim();
}

function applyCalendarSearch(panel, value) {
  const query = normalizeCalendarSearch(value);
  const items = Array.from(panel.querySelectorAll('.loomly-ai-calendar-panel-item'));
  const groups = Array.from(panel.querySelectorAll('.loomly-ai-calendar-group'));

  const matchedGroupTargets = new Set();
  groups.forEach((group) => {
    const name = group.querySelector('.loomly-ai-calendar-group-name');
    if (query && normalizeCalendarSearch(name?.textContent).includes(query)) {
      matchedGroupTargets.add(group.getAttribute('data-target'));
    }
  });

  let visibleCount = 0;
  items.forEach((item) => {
    const inMatchedGroup = Array.from(matchedGroupTargets)
      .some((selector) => selector && item.closest(selector));
    const name = item.querySelector('.loomly-ai-calendar-panel-name');
    const haystack = normalizeCalendarSearch(name?.textContent || item.textContent);
    const matches = !query || inMatchedGroup || haystack.includes(query);

    item.hidden = !matches;
    if (matches) visibleCount += 1;
  });

  groups.forEach((group) => {
    const selector = group.getAttribute('data-target');
    const children = selector ? panel.querySelector(selector) : null;
    const childItems = children
      ? Array.from(children.querySelectorAll('.loomly-ai-calendar-panel-item'))
      : [];
    const hasMatch = matchedGroupTargets.has(selector) || childItems.some((item) => !item.hidden);

    group.hidden = !hasMatch;
    if (!children) return;

    children.hidden = !hasMatch;

    if (query) {
      // Remember how the user had the group, so clearing the search restores it.
      if (group.dataset.preSearchCollapsed === undefined) {
        group.dataset.preSearchCollapsed = group.classList.contains('collapsed') ? '1' : '0';
      }
      group.classList.toggle('collapsed', !hasMatch);
      children.classList.toggle('show', hasMatch);
      return;
    }

    const wasCollapsed = group.dataset.preSearchCollapsed !== '0';
    group.classList.toggle('collapsed', wasCollapsed);
    children.classList.toggle('show', !wasCollapsed);
    delete group.dataset.preSearchCollapsed;
  });

  const allCalendarsItem = panel.querySelector('.loomly-ai-calendar-panel-item--all');
  const divider = panel.querySelector('.loomly-ai-calendar-panel-divider');
  if (divider && allCalendarsItem) divider.hidden = allCalendarsItem.hidden;

  const noResults = panel.querySelector('.loomly-ai-calendar-panel-no-results');
  if (noResults) noResults.hidden = !(query && visibleCount === 0);
}

function resetCalendarSearch(panel) {
  const input = panel.querySelector('.js-calendar-panel-search');
  if (!input || input.value === '') return;

  input.value = '';
  applyCalendarSearch(panel, '');
}

// Without focus the keystrokes land on the toggle button and the field looks dead.
function focusCalendarSearch(panel) {
  const input = panel.querySelector('.js-calendar-panel-search');
  if (!input) return;

  input.focus({ preventScroll: true });
  const end = input.value.length;
  input.setSelectionRange?.(end, end);
}

function closeCalendarPanel(panel) {
  if (panel.dataset.visible !== 'true') return;

  panel.dataset.visible = 'false';
  document.querySelector('.js-calendar-panel-toggle')?.classList.remove('active');
  hideFloatingTooltip();
}

function initCalendarPanelSearch(panel) {
  const input = panel.querySelector('.js-calendar-panel-search');
  if (!input) return;

  input.addEventListener('input', () => applyCalendarSearch(panel, input.value));

  // Escape clears the filter first, then closes the panel.
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    if (input.value !== '') {
      event.preventDefault();
      event.stopPropagation();
      input.value = '';
      applyCalendarSearch(panel, '');
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    closeCalendarPanel(panel);
  });

  // The native clear control does not always emit `input`.
  input.addEventListener('search', () => applyCalendarSearch(panel, input.value));
}

function initCalendarSelectorPanel() {
  const toggle = document.querySelector('.js-calendar-panel-toggle');
  const panel = document.getElementById('loomly-ai-calendar-panel');

  if (!toggle || !panel) {
    return;
  }

  if (panel.dataset.initialized === 'true') return;
  panel.dataset.initialized = 'true';

  initCalendarPanelSearch(panel);

  panel.addEventListener('mouseenter', (e) => {
    const name = e.target.closest('.loomly-ai-calendar-panel-name, .loomly-ai-calendar-group-name');
    if (name) showFloatingTooltip(name);
  }, true);

  panel.addEventListener('mouseleave', hideFloatingTooltip, true);

  // Whatever closed the panel, neither the label nor an old search may survive it.
  new MutationObserver(() => {
    if (panel.dataset.visible === 'true') return;

    hideFloatingTooltip();
    resetCalendarSearch(panel);
  }).observe(panel, { attributes: true, attributeFilter: ['data-visible'] });

  bindTooltipDismissal();

  toggle.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isVisible = panel.dataset.visible === 'true';
    panel.dataset.visible = (!isVisible).toString();

    if (!isVisible) {
      toggle.classList.add('active');
      focusCalendarSearch(panel);
    } else {
      toggle.classList.remove('active');
      hideFloatingTooltip();
    }

    if (window.trackPicoUserAction) {
      const uiState = window.getLoomlyAiUiState ? window.getLoomlyAiUiState() : {};
      window.trackPicoUserAction('sidebar_calendar_selector_toggled', { ...uiState, open: !isVisible });
    }
  });

  // Close panel when clicking outside
  document.addEventListener('click', (e) => {
    if (panel.dataset.visible === 'true' &&
      !panel.contains(e.target) &&
      !toggle.contains(e.target)) {
      closeCalendarPanel(panel);
    }
  });

  // Handle calendar group accordion
  panel.querySelectorAll('.loomly-ai-calendar-group').forEach(group => {
    const header = group.querySelector('.loomly-ai-calendar-group-header');
    const targetId = group.getAttribute('data-target');
    const target = document.querySelector(targetId);

    if (!header || !target) return;

    header.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const isCollapsed = group.classList.contains('collapsed');

      if (isCollapsed) {
        group.classList.remove('collapsed');
        target.classList.add('show');
      } else {
        group.classList.add('collapsed');
        target.classList.remove('show');
      }
    });
  });
}

document.addEventListener('turbo:load', initCalendarSelectorPanel);
document.addEventListener('DOMContentLoaded', initCalendarSelectorPanel);

